/**
 * Lorry orders sent from this screen — and each one's RETURN order.
 *
 * Read LIVE from Smartlink, not from the 15-minute copy the board uses: the first lorry test
 * (2026-10-04) was submitted and the board still said "picked 0".
 *
 * The return (LSK's choice, 2026-10-04): once the pick is submitted, one button pushes a planned
 * order LOADING → HQ1 listing everything picked, and the crew scan only what comes back. Preview
 * first, as everywhere else — a pushed order is on a handheld at once and locked after a scan.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api';
import { Table, Loading, Empty, cell } from '../components/ui';

type ReturnLeg = {
  orderNo: string;
  status: number | null;
  planned: number | null;
  scanned: number | null;
  submittedAt: string | null;
  cancelled: boolean;
};

export type SentLorry = {
  orderNo: string;
  plate: string;
  shipDate: string;
  salesOrders: string[];
  createdBy: string | null;
  createdAt: string;
  status: number | null;
  planned: number | null;
  scanned: number | null;
  submittedAt: string | null;
  cancelled: boolean;
  canCreateReturn: boolean;
  /** Pending with nothing scanned — the plan can still be replaced (LSK, 2026-10-31). */
  canEdit?: boolean;
  return: ReturnLeg | null;
};

type ReturnPreview = {
  orderNo: string;
  payload?: { planQty: number; data: Array<{ sku: string; planQty: number }> };
  simulated?: boolean;
};

/** Smartlink header status (CLAUDE.md §4.3) — 3 is a FAILED submit, not pending. */
const STATUS: Record<number, string> = {
  1: 'pending',
  2: 'being scanned',
  3: 'submit failed',
  10: 'submitted',
};
const statusText = (s: number | null, cancelled: boolean): string =>
  cancelled ? 'cancelled' : s === null ? '—' : (STATUS[s] ?? `status ${s}`);
/** Minute precision is what anyone reads; the seconds are noise on a screen. */
const when = (t: string | null): string => (t ? t.slice(0, 16) : '—');

type DetailRow = {
  docno: string | null;
  itemcode: string;
  planned: string;
  scanned: string;
  returned: string;
  out: string;
};

/**
 * One lorry order, per sales order (LSK, 2026-10-04). Scans are per item for the whole lorry and
 * are split first sales order first — the rule the Load tab states — with anything beyond plan on
 * the last line wanting the item, so an over-scan stays visible.
 */
function LorryDetail({ orderNo }: { orderNo: string }): JSX.Element {
  const detail = useQuery({
    queryKey: ['lorry', 'detail', orderNo],
    queryFn: () =>
      api.get<{ rows: DetailRow[]; rebuilt: boolean; unmatched: string[] }>(
        `/api/v1/rfid/lorry/orders/${encodeURIComponent(orderNo)}`,
      ),
  });
  if (detail.isLoading) return <Loading />;
  if (detail.isError) {
    return (
      <p className="rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
        {(detail.error as Error).message}
      </p>
    );
  }
  const rows = detail.data?.rows ?? [];
  let lastSo: string | null | undefined;
  return (
    <div className="space-y-2 rounded border border-slate-200 bg-white px-4 py-3">
      <Table
        head={['Sales order', 'Item', 'Planned', 'Scanned', 'Returned', 'Actually out']}
        rows={rows.map((r) => {
          // The sales order once per group — the rows arrive in lorry order.
          const so = r.docno === lastSo ? '' : (r.docno ?? 'not on any sales order');
          lastSo = r.docno;
          const over = Number(r.scanned) > Number(r.planned);
          return [
            cell(r.docno ?? '', <span className="font-mono text-xs">{so}</span>),
            cell(r.itemcode, <span className="font-mono text-xs">{r.itemcode}</span>),
            cell(Number(r.planned), <span>{Number(r.planned)}</span>),
            cell(
              Number(r.scanned),
              <span className={over ? 'font-semibold text-amber-700' : ''}>
                {Number(r.scanned)}
              </span>,
            ),
            cell(Number(r.returned), <span>{Number(r.returned)}</span>),
            cell(Number(r.out), <strong>{Number(r.out)}</strong>),
          ];
        })}
      />
      <p className="text-xs text-slate-500">
        The handheld counts per item for the whole lorry; it is split here first sales order first.
        Amber means more was scanned than planned.
      </p>
      {detail.data?.rebuilt && (
        <p className="text-xs text-amber-800">
          Sent before the per-order plan was saved, so the planned quantities are rebuilt from the
          sales orders as they are now — they may differ if those orders have changed since.
        </p>
      )}
      {(detail.data?.unmatched.length ?? 0) > 0 && (
        <p className="text-xs text-amber-800">
          Scanned but not matched to an item: {detail.data?.unmatched.join(', ')}
        </p>
      )}
    </div>
  );
}

export function LorryOrders({
  onEdit,
}: {
  /** Load this lorry order back into the builder. */
  onEdit?: (orderNo: string) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [returning, setReturning] = useState<string | null>(null);
  const [preview, setPreview] = useState<ReturnPreview | null>(null);
  const [sent, setSent] = useState<ReturnPreview | null>(null);

  const orders = useQuery({
    queryKey: ['lorry', 'orders'],
    queryFn: () => api.get<{ orders: SentLorry[] }>('/api/v1/rfid/lorry/orders'),
    refetchInterval: 60_000,
  });

  const makeReturn = useMutation({
    mutationFn: (v: { orderNo: string; confirm: boolean }) =>
      api.post<ReturnPreview>(
        `/api/v1/rfid/lorry/${encodeURIComponent(v.orderNo)}/return`,
        v.confirm ? { confirm: true } : {},
      ),
    onSuccess: (r, v) => {
      if (v.confirm) {
        setSent(r);
        setPreview(null);
        void qc.invalidateQueries({ queryKey: ['lorry'] });
      } else setPreview(r);
    },
  });

  if (orders.isLoading) return <Loading />;
  const list = orders.data?.orders ?? [];
  if (list.length === 0) return <Empty>No lorry orders sent from here in the last 30 days.</Empty>;

  return (
    <div className="space-y-3">
      <Table
        head={[
          'Lorry order',
          'Ship date',
          'Sales orders',
          'Status',
          'Planned',
          'Scanned',
          'Created',
          'Submitted',
          'Return',
        ]}
        rows={list.map((o) => [
          cell(
            o.orderNo,
            <button
              type="button"
              onClick={() => setOpen((cur) => (cur === o.orderNo ? null : o.orderNo))}
              aria-expanded={open === o.orderNo}
              className="font-mono text-xs text-brand-700 underline decoration-dotted hover:text-brand-900"
            >
              {o.orderNo}
            </button>,
          ),
          o.shipDate,
          cell(
            o.salesOrders.join(', '),
            <span className="text-xs">{o.salesOrders.join(', ') || '—'}</span>,
          ),
          cell(
            statusText(o.status, o.cancelled),
            <span className="text-xs">
              {statusText(o.status, o.cancelled)}
              {o.canEdit && onEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(o.orderNo)}
                  className="ml-2 rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-brand-50"
                >
                  Edit
                </button>
              )}
            </span>,
          ),
          cell(o.planned ?? -1, <span>{o.planned ?? '—'}</span>),
          cell(o.scanned ?? -1, <span>{o.scanned ?? '—'}</span>),
          cell(
            o.createdAt,
            <span className="text-xs">
              {when(o.createdAt)}
              {o.createdBy && <span className="text-slate-500"> · {o.createdBy}</span>}
            </span>,
          ),
          cell(o.submittedAt ?? '', <span className="text-xs">{when(o.submittedAt)}</span>),
          o.return
            ? cell(
                o.return.orderNo,
                <span className="text-xs">
                  <span className="font-mono">{o.return.orderNo}</span>
                  <br />
                  {statusText(o.return.status, o.return.cancelled)} · {o.return.scanned ?? 0} of{' '}
                  {o.return.planned ?? 0} back
                </span>,
              )
            : o.canCreateReturn
              ? cell(
                  '',
                  <button
                    type="button"
                    disabled={makeReturn.isPending}
                    onClick={() => {
                      setReturning(o.orderNo);
                      setSent(null);
                      makeReturn.mutate({ orderNo: o.orderNo, confirm: false });
                    }}
                    className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50 disabled:text-slate-400"
                  >
                    Create return order
                  </button>,
                )
              : cell(
                  '',
                  <span className="text-xs text-slate-400">
                    {o.cancelled ? '—' : 'after the pick is submitted'}
                  </span>,
                ),
        ])}
      />

      {open && (
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-slate-700">
            <span className="font-mono">{open}</span> — per sales order
          </h3>
          <LorryDetail orderNo={open} />
        </div>
      )}

      {makeReturn.isError && (
        <p className="rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
          {(makeReturn.error as Error).message}
        </p>
      )}

      {preview && returning && (
        <div className="space-y-2 rounded border border-slate-200 bg-brand-50 px-4 py-3 text-sm text-slate-700">
          <p>
            Return order <strong className="font-mono">{preview.orderNo}</strong>, loading area →
            HQ1. Everything picked is listed; the crew scan only what comes back. The handheld will
            warn that it is short — that is expected.
          </p>
          <Table
            head={['Scan code', 'Picked']}
            rows={(preview.payload?.data ?? []).map((d) => [
              <span className="font-mono text-xs">{d.sku}</span>,
              cell(d.planQty, <span>{d.planQty}</span>),
            ])}
          />
          <button
            type="button"
            disabled={makeReturn.isPending}
            onClick={() => makeReturn.mutate({ orderNo: returning, confirm: true })}
            className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:bg-slate-300"
          >
            {makeReturn.isPending ? 'Sending…' : 'Send return to handheld'}
          </button>
        </div>
      )}

      {sent && (
        <p className="rounded border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {sent.simulated ? (
            <>Simulated — DRY_RUN is on, nothing was sent.</>
          ) : (
            <>
              Return sent as <strong className="font-mono">{sent.orderNo}</strong>. It should appear
              on the handheld at HQ1 now.
            </>
          )}
        </p>
      )}
    </div>
  );
}
